import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import atsService from '../services/atsService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--spacing-6);
  background: var(--color-bg);
`;

const Card = styled.div`
  background: var(--color-surface-2);
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  padding: var(--spacing-8);
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-4);
`;

const Message = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-6);
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--color-line);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-6);

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-error-bg);
  color: var(--color-error);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-4);
  font-size: 24px;
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-success-bg);
  color: var(--color-success);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-4);
  font-size: 24px;
`;

const Button = styled.button`
  padding: var(--spacing-3) var(--spacing-6);
  background: var(--color-primary);
  color: var(--color-ink);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--transition-default);

  &:hover {
    background: var(--color-primary-dark);
  }
`;

type CallbackStatus = 'processing' | 'success' | 'error' | 'auth_required';

const IntegrationsCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      localStorage.setItem('ats_callback_return_url', location.pathname + location.search);
      setStatus('auth_required');
      return;
    }

    const completeConnection = async () => {
      try {
        const publicToken = searchParams.get('public_token');
        const state = localStorage.getItem('ats_connection_state');

        if (!publicToken) {
          setStatus('error');
          setErrorMessage('No authentication token received. The connection may have been cancelled.');
          return;
        }

        if (!state) {
          setStatus('error');
          setErrorMessage('Connection state not found. Please try connecting again from the Integrations page.');
          return;
        }

        await atsService.completeConnection({
          publicToken,
          state,
        });

        localStorage.removeItem('ats_connection_state');
        localStorage.removeItem('ats_callback_return_url');
        setStatus('success');

        setTimeout(() => {
          navigate('/integrations', { 
            state: { connectionSuccess: true },
            replace: true 
          });
        }, 2000);
      } catch (error: any) {
        setStatus('error');
        const message = error.response?.data?.message || error.message || 'Failed to complete the connection';
        setErrorMessage(message);
        localStorage.removeItem('ats_connection_state');
      }
    };

    completeConnection();
  }, [searchParams, navigate, user, authLoading, location.pathname, location.search]);

  const handleRetry = () => {
    navigate('/integrations', { replace: true });
  };

  const handleLogin = () => {
    navigate('/auth/login', { replace: true });
  };

  return (
    <Container>
      <Card>
        {(status === 'processing' || authLoading) && (
          <>
            <Spinner />
            <Title>Completing Connection</Title>
            <Message>Please wait while we finalise your ATS connection...</Message>
          </>
        )}

        {status === 'success' && (
          <>
            <SuccessIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </SuccessIcon>
            <Title>Connection Successful</Title>
            <Message>Your ATS has been connected successfully. Redirecting to Integrations...</Message>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </ErrorIcon>
            <Title>Connection Failed</Title>
            <Message>{errorMessage}</Message>
            <Button onClick={handleRetry}>Return to Integrations</Button>
          </>
        )}

        {status === 'auth_required' && (
          <>
            <ErrorIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </ErrorIcon>
            <Title>Login Required</Title>
            <Message>Please log in to complete your ATS connection. Your connection progress has been saved.</Message>
            <Button onClick={handleLogin}>Log In</Button>
          </>
        )}
      </Card>
    </Container>
  );
};

export default IntegrationsCallbackPage;
